"use client";

import clsx from "clsx";
import { useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";

import IconImageSparkle from "@ui/central-icons/icons/IconImageSparkle";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { AnimatedDotGrid } from "@ui/components/core/AnimatedDotGrid";
import { RandomFact } from "@ui/components/core/RandomFact";
import { cn } from "@ui/lib/utils";

/**
 * The result column's running state, shared by the standalone `BlockFlowTemplate`
 * and the gallery modal. Mirrors the InteractiveImage demo's generating surface —
 * a solid card with a drifting dot grid behind a shimmering "Generating…" line and
 * a rotating "Did you know?" fact to fill the wait. Decorative (`aria-hidden`) so
 * it doesn't double-read against the caller's SR status.
 */
export function FlowTemplateRunningPlaceholder({ className }: { className?: string }) {
  const t = useTranslations("ClientKeys.flow_template_gallery");
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <div
      className={cn(
        "tw-relative tw-aspect-square tw-w-full tw-overflow-hidden tw-rounded-[20px] tw-bg-gray-200",
        className,
      )}
      aria-hidden
    >
      <AnimatedDotGrid reduceMotion={reduceMotion} />
      {/* Soft radial wash in the card colour so the copy stays legible over the
          drifting dots. */}
      <div className='tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-h-32 tw-w-96 tw-max-w-[90%] -tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-gray-200 [-webkit-mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)]' />
      <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-px-6'>
        <div className='tw-relative tw-flex tw-w-80 tw-max-w-full tw-flex-col tw-items-center'>
          <p className='shimmer-text tw-py-1 tw-leading-normal tw-type-sm'>{t("result_running")}</p>
          <RandomFact />
        </div>
      </div>
    </div>
  );
}

/**
 * The result column's idle state, shown before a run starts — a dashed preview
 * frame with a sparkle + "your result will appear here" hint. Shared by the
 * standalone `BlockFlowTemplate` and the gallery modal so both read identically.
 */
export function FlowTemplateIdlePlaceholder({ className }: { className?: string }) {
  const t = useTranslations("ClientKeys.flow_template_gallery");

  return (
    <div
      className={cn(
        "tw-flex tw-aspect-square tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-[20px] tw-border tw-border-dashed tw-border-black/10 tw-bg-gray-25 tw-text-gray-400",
        className,
      )}
    >
      <IconImageSparkle className={clsx(iconStrokeFix, "tw-size-8")} />
      <p className='tw-type-sm tw-text-gray-500'>{t("result_placeholder")}</p>
    </div>
  );
}
